import { useEffect, useMemo, useState } from 'react'
import type { OrganisationAccessContext } from './service-access-api'

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

export const SWELL_HELP_CENTRE_URL = 'https://fifthwavedigital.com/help/'

type ServiceAccess = OrganisationAccessContext['service']

export type LicenceDisplay = {
  licenceLabel: string
  sidebarLabel: string
  timeRemainingLabel: string | null
  trialEndLabel: string | null
  tone: 'neutral' | 'approaching' | 'expired'
}

function plural(value: number, singular: string) {
  return `${value} ${singular}${value === 1 ? '' : 's'}`
}

export function formatTrialTimeRemaining(trialEndsAt: string | null, now = new Date()): string | null {
  if (!trialEndsAt) return null

  const trialEnd = new Date(trialEndsAt)
  if (Number.isNaN(trialEnd.getTime())) return null

  const remainingMs = trialEnd.getTime() - now.getTime()
  if (remainingMs <= 0) return 'Trial expired'

  if (remainingMs > 48 * HOUR_MS) {
    return `${plural(Math.floor(remainingMs / DAY_MS), 'day')} remaining`
  }

  const remainingHours = Math.ceil(remainingMs / HOUR_MS)
  const days = Math.floor(remainingHours / 24)
  const hours = remainingHours % 24
  const parts = [
    ...(days ? [plural(days, 'day')] : []),
    ...(hours ? [plural(hours, 'hour')] : []),
  ]

  return `${parts.join(' ')} remaining`
}

export function formatTrialEndDate(trialEndsAt: string | null): string | null {
  if (!trialEndsAt) return null

  const trialEnd = new Date(trialEndsAt)
  if (Number.isNaN(trialEnd.getTime())) return null

  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(trialEnd)
}

export function getLicenceDisplay(service: ServiceAccess, now = new Date()): LicenceDisplay {
  if (service.status === 'trial') {
    const timeRemainingLabel = formatTrialTimeRemaining(service.trialEndsAt, now)
    const expired = timeRemainingLabel === 'Trial expired'
    const trialEnd = service.trialEndsAt ? new Date(service.trialEndsAt) : null
    const remainingMs = trialEnd && !Number.isNaN(trialEnd.getTime())
      ? trialEnd.getTime() - now.getTime()
      : null

    return {
      licenceLabel: 'Trial',
      sidebarLabel: expired
        ? 'Trial expired'
        : timeRemainingLabel
          ? `Trial · ${timeRemainingLabel}`
          : 'Trial',
      timeRemainingLabel: expired ? 'Expired' : timeRemainingLabel,
      trialEndLabel: formatTrialEndDate(service.trialEndsAt),
      tone: expired ? 'expired' : remainingMs !== null && remainingMs <= 48 * HOUR_MS ? 'approaching' : 'neutral',
    }
  }

  if (service.status === 'provisioning') {
    return {
      licenceLabel: 'Being prepared',
      sidebarLabel: 'Licence being prepared',
      timeRemainingLabel: null,
      trialEndLabel: null,
      tone: 'neutral',
    }
  }

  return {
    licenceLabel: 'Full licence',
    sidebarLabel: 'Full licence',
    timeRemainingLabel: null,
    trialEndLabel: null,
    tone: 'neutral',
  }
}

export function useLicenceDisplay(service: ServiceAccess, nowOverride?: Date) {
  const [currentTime, setCurrentTime] = useState(() => new Date())

  useEffect(() => {
    if (nowOverride) return undefined
    const interval = window.setInterval(() => setCurrentTime(new Date()), 60_000)
    return () => window.clearInterval(interval)
  }, [nowOverride])

  return useMemo(
    () => getLicenceDisplay(service, nowOverride ?? currentTime),
    [currentTime, nowOverride, service],
  )
}
